import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TOUR_STEPS, TOUR_SEEN_KEY } from "./steps";

const TourContext = createContext(null);

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used inside TourProvider");
  return ctx;
};

export const TourProvider = ({ children }) => {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [prompted, setPrompted] = useState(false); // first-login soft prompt
  const nav = useNavigate();

  const start = useCallback((fromBeginning = true) => {
    if (fromBeginning) setStepIdx(0);
    setActive(true);
    setPrompted(false);
  }, []);

  const stop = useCallback((markSeen = true) => {
    setActive(false);
    setStepIdx(0);
    if (markSeen) {
      try { localStorage.setItem(TOUR_SEEN_KEY, "1"); } catch (_) { /* ignore */ }
    }
  }, []);

  const next = useCallback(() => {
    setStepIdx((i) => {
      const n = i + 1;
      if (n >= TOUR_STEPS.length) {
        setActive(false);
        try { localStorage.setItem(TOUR_SEEN_KEY, "1"); } catch (_) { /* ignore */ }
        return 0;
      }
      return n;
    });
  }, []);

  const prev = useCallback(() => {
    setStepIdx((i) => Math.max(0, i - 1));
  }, []);

  // Navigate when a step specifies a route
  useEffect(() => {
    if (!active) return;
    const step = TOUR_STEPS[stepIdx];
    if (step?.route) nav(step.route);
  }, [active, stepIdx, nav]);

  // Offer the tour on first login
  useEffect(() => {
    const seen = (() => {
      try { return localStorage.getItem(TOUR_SEEN_KEY); } catch (_) { return "1"; }
    })();
    if (!seen) {
      const t = setTimeout(() => setPrompted(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const dismissPrompt = useCallback(() => {
    setPrompted(false);
    try { localStorage.setItem(TOUR_SEEN_KEY, "1"); } catch (_) { /* ignore */ }
  }, []);

  const value = {
    active, stepIdx, prompted,
    step: TOUR_STEPS[stepIdx],
    total: TOUR_STEPS.length,
    start, stop, next, prev, dismissPrompt,
  };
  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};
